<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="show" class="connect-modal-overlay" @click="$emit('close')">
        <div class="connect-modal" @click.stop>
          <div class="connect-modal__header">
            <div class="connect-modal__header-content">
              <div class="connect-modal__icon-container">
                <Icon :icon="channelIcon" class="connect-modal__icon" />
              </div>
              <div class="connect-modal__title-group">
                <h4 class="connect-modal__title">Connect {{ channelName }}</h4>
                <p class="connect-modal__subtitle">
                  Authenticate your {{ channelName }} account to start messaging through Unipile
                  API.
                </p>
              </div>
            </div>
            <button type="button" class="connect-modal__close" @click="$emit('close')">
              <Icon icon="mdi:close" />
            </button>
          </div>
          <div class="connect-modal__body">
            <div class="connect-modal__features">
              <h5 class="connect-modal__features-title">Features:</h5>
              <div class="connect-modal__features-list">
                <div class="connect-modal__features-column">
                  <div
                    v-for="feature in channelFeatures.slice(0, 2)"
                    :key="feature"
                    class="connect-modal__feature-item"
                  >
                    <Icon icon="mdi:check-circle" class="connect-modal__feature-check" />
                    <span>{{ feature }}</span>
                  </div>
                </div>
                <div class="connect-modal__features-column">
                  <div
                    v-for="feature in channelFeatures.slice(2)"
                    :key="feature"
                    class="connect-modal__feature-item"
                  >
                    <Icon icon="mdi:check-circle" class="connect-modal__feature-check" />
                    <span>{{ feature }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Account type selector for email -->
            <div v-if="channel?.type === 'email'" class="connect-modal__account-type">
              <Select
                v-model="selectedAccountType"
                label="Account Type"
                placeholder="Select account type"
                :options="accountTypeOptions"
                :error="
                  showAccountTypeError ? 'Account type is required for email channel' : undefined
                "
                required
              />
            </div>
            <button
              class="connect-modal__connect-btn"
              :class="{ 'connect-modal__connect-btn--loading': isLoading }"
              @click="handleConnect"
              :disabled="isLoading || (channel?.type === 'email' && !selectedAccountType)"
            >
              <Icon
                v-if="isLoading"
                icon="mdi:loading"
                class="connect-modal__connect-icon connect-modal__connect-icon--spinning"
              />
              <Icon v-else icon="mdi:link-variant" class="connect-modal__connect-icon" />
              {{ isLoading ? "Connecting..." : `Connect ${channelName}` }}
            </button>
            <p class="connect-modal__security-note">
              Secured by Unipile API • Your credentials are encrypted.
            </p>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Icon } from "@iconify/vue";
import { Select } from "@/components/ui";
import type { Channel } from "@/dtos/Unipile.dto";
import type { SelectOption } from "@/components/ui/types";

interface Props {
  show: boolean;
  channel?: Channel | null;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const emit = defineEmits<{
  close: [];
  connect: [channel: Channel, accountType?: string];
}>();

const selectedAccountType = ref<string>("");
const showAccountTypeError = ref(false);

const accountTypeOptions: SelectOption[] = [
  { value: "GOOGLE", label: "Google" },
  { value: "OUTLOOK", label: "Microsoft Outlook" },
];

// Reset account type when modal closes or channel changes
watch(
  () => [props.show, props.channel?.type],
  () => {
    if (!props.show || props.channel?.type !== "email") {
      selectedAccountType.value = "";
      showAccountTypeError.value = false;
    }
  }
);

const channelName = computed(() => props.channel?.name || "Channel");
const channelIcon = computed(() => props.channel?.icon || "mdi:link-variant");

const channelFeatures = computed(() => {
  const channelType = props.channel?.type;
  if (channelType === "email") {
    return ["Messaging", "File Sharing", "Read Receipts", "Thread Management"];
  } else if (channelType === "linkedin") {
    return ["Messaging", "InMail", "Connection Requests", "Read Receipts"];
  }
  return ["Messaging", "File Sharing", "Voice Messages", "Read Receipts"];
});

const handleConnect = () => {
  if (!props.channel) return;

  // Validate account type for email
  if (props.channel.type === "email" && !selectedAccountType.value) {
    showAccountTypeError.value = true;
    return;
  }

  showAccountTypeError.value = false;
  emit("connect", props.channel, selectedAccountType.value || undefined);
};
</script>

<style scoped>
.connect-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10060;
}

.connect-modal {
  background: white;
  border-radius: 12px;
  max-width: 420px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.connect-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.connect-modal__header-content {
  display: flex;
  gap: 0.75rem;
  flex: 1;
}

.connect-modal__icon-container {
  width: 36px;
  height: 36px;
  background: #7c3aed;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.connect-modal__icon {
  font-size: 1.125rem;
  color: white;
}

.connect-modal__title-group {
  flex: 1;
}

.connect-modal__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.connect-modal__subtitle {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
}

.connect-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.connect-modal__close:hover {
  color: #111827;
}

.connect-modal__body {
  padding: 1.25rem;
}

.connect-modal__features {
  margin-bottom: 1.25rem;
}

.connect-modal__features-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.625rem 0;
}

.connect-modal__features-list {
  display: flex;
  gap: 1rem;
}

.connect-modal__features-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.connect-modal__feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #4b5563;
}

.connect-modal__feature-check {
  color: #10b981;
  font-size: 1rem;
}

.connect-modal__connect-btn {
  width: 100%;
  padding: 0.625rem 1rem;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.2s;
  margin-bottom: 0.875rem;
}

.connect-modal__connect-btn:hover:not(:disabled) {
  background: #6d28d9;
}

.connect-modal__connect-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.connect-modal__connect-icon {
  transition: transform 0.2s;
}

.connect-modal__connect-icon--spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.connect-modal__account-type {
  margin-bottom: 1.25rem;
}

.connect-modal__security-note {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
  margin: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
